import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const inputCls = "w-full bg-white rounded-md px-4 py-3.5 text-sm text-gray-900 mb-6 outline-none focus:ring-2 focus:ring-[#c9a96e]/40";
const labelCls = "block text-[10px] uppercase tracking-[2.5px] text-white/45 font-medium mb-2";

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    username: '', email: '', password: '', first_name: '', last_name: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const res = await register(formData);
      // New users get pending message, not auto-login
      setSuccess(res?.data?.message || 'Access request submitted. An administrator will review your account.');
    } catch (err) {
      const data = err.response?.data;
      if (data?.message) {
        setSuccess(data.message);
      } else if (data) {
        setError(Object.values(data).flat().join(' '));
      } else {
        setError('Registration failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0a0a0a] text-white">
      {/* Left panel */}
      <div className="flex-1 flex-col justify-between p-12 border-r border-white/5 hidden lg:flex">
        <div className="flex items-center gap-5">
          <div className="font-serif text-xl">Maghaweer<span className="text-[#c9a96e]">.ai</span></div>
          <div className="text-[10px] uppercase tracking-[3px] text-white/35 leading-tight">CAPITAL<br />MARKETS<br />INTELLIGENCE</div>
        </div>
        <div className="flex-1 flex flex-col justify-center max-w-[480px]">
          <div className="text-[10px] uppercase tracking-[4px] text-[#c9a96e] mb-6 font-medium">INTERNAL PLATFORM</div>
          <h1 className="font-serif text-5xl font-normal leading-[1.1] mb-8">Capital<br />markets<br /><em className="text-[#c9a96e]">intelligence.</em></h1>
          <p className="text-sm text-white/40 leading-relaxed max-w-[380px]">A consolidated intelligence environment for the capital markets team. Every tool, signal, and dataset &mdash; in one place.</p>
        </div>
        <div className="flex gap-10 pt-5">
          <div className="text-[9px] uppercase tracking-[3px] text-white/25 font-medium">RESTRICTED<br />ACCESS</div>
          <div className="text-[9px] uppercase tracking-[3px] text-white/25 font-medium">ENCRYPTED</div>
          <div className="text-[9px] uppercase tracking-[3px] text-white/25 font-medium">SESSION<br />MONITORED</div>
        </div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-[520px] shrink-0 flex flex-col justify-center px-6 lg:px-14 py-12 bg-[#0f0f0f]">
        <div className="lg:hidden mb-8 text-center pb-8 border-b border-white/5">
          <div className="font-serif text-2xl">Maghaweer<span className="text-[#c9a96e]">.ai</span></div>
          <div className="text-[9px] uppercase tracking-[3px] text-white/30 mt-2">CAPITAL MARKETS INTELLIGENCE</div>
        </div>

        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[3px] text-[#c9a96e] mb-10 font-medium">
          <span className="w-1.5 h-1.5 rounded-full bg-[#c9a96e]" />
          REQUEST ACCESS
        </div>

        <h2 className="font-serif text-3xl font-normal mb-1.5">Create account</h2>
        <p className="text-sm text-white/35 mb-10">All accounts require admin approval</p>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-900/20 border border-red-800/30 text-red-400 text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-green-900/20 border border-green-800/30 text-green-400 text-sm">
            {success}
            <div className="mt-2">
              <Link to="/login" className="text-[#c9a96e] font-medium hover:text-[#dfc08a]">Return to login</Link>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className={labelCls}>FIRST NAME</label>
              <input className={inputCls} name="first_name" value={formData.first_name} onChange={handleChange} />
            </div>
            <div className="flex-1">
              <label className={labelCls}>LAST NAME</label>
              <input className={inputCls} name="last_name" value={formData.last_name} onChange={handleChange} />
            </div>
          </div>

          <label className={labelCls}>USERNAME</label>
          <input className={inputCls} name="username" value={formData.username} onChange={handleChange} required autoFocus />

          <label className={labelCls}>EMAIL</label>
          <input className={inputCls} name="email" type="email" value={formData.email} onChange={handleChange} required />

          <label className={labelCls}>PASSWORD</label>
          <input className={inputCls} name="password" type="password" value={formData.password} onChange={handleChange} required minLength={8} />

          <button
            type="submit"
            className="w-full py-3.5 border border-white/15 rounded-md text-xs uppercase tracking-[3px] font-medium bg-transparent text-white cursor-pointer transition-all mt-2 hover:bg-white/5 hover:border-[#c9a96e] hover:text-[#c9a96e] disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'CREATING...' : 'REQUEST ACCESS'}
          </button>
        </form>

        <div className="h-px bg-white/5 my-8" />
        <p className="text-xs text-white/25 leading-relaxed">Access is restricted to authorised capital markets personnel. All sessions are logged. Unauthorised access is strictly prohibited.</p>
        <div className="text-center mt-6 text-sm text-white/35">
          Already have an account? <Link to="/login" className="text-[#c9a96e] font-medium hover:text-[#dfc08a]">Sign in</Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
